import { useCallback, useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { useT } from '@/shared/i18n'
import { jsonFetch } from '@/shared/lib/jsonFetch'
import {
  collectFeedbackDiagnostics,
  installFeedbackDiagnostics,
} from '@/shared/lib/feedbackDiagnostics'
import { Button } from './Button'
import { Checkbox } from './Checkbox'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { SelectMenu, type SelectMenuOption } from './SelectMenu'
import { Textarea } from './Textarea'

// ---------------------------------------------------------------------------
// FeedbackWidget — the floating "Meld feil" trigger, shared by all three
// surfaces.
//
// Flow: clicking the trigger captures the page BEFORE the dialog mounts, so
// the capture photographs the bug rather than this component sitting on top of
// it. The dialog then collects a category and a description, with an
// on-by-default checkbox for attaching the capture.
//
// Submits with jsonFetch, never an Inertia visit: a floating widget must not
// navigate the page away, and the endpoint answers JSON.
//
// Reads the server-owned `feedback` shared prop (enabled flag + category
// vocabulary) the same way PortalShell reads its own shared props — no
// fetching, no privileged endpoints, no surface assumptions. Renders nothing
// when the surface's kill switch is off; the endpoint enforces the same flag,
// so this is convenience, not the fence.
// ---------------------------------------------------------------------------

const ENDPOINT = '/tilbakemelding'

// Mirrors FeedbackReport::SCREENSHOT_MAX_BYTES. A capture over the cap is
// dropped client-side rather than posted for the server to reject.
const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024

type FeedbackShare = {
  enabled?: boolean
  categories?: SelectMenuOption[]
}

/**
 * Photograph the current page. Returns null on any failure — a broken capture
 * must never stop someone from reporting the bug they came to report.
 */
async function capturePage(): Promise<Blob | null> {
  try {
    // Dynamically imported so the capture library downloads only when someone
    // actually clicks, and never weighs on the public register bundle.
    const { domToBlob } = await import('modern-screenshot')
    const blob = await domToBlob(document.body, {
      type: 'image/webp',
      quality: 0.8,
      // WebP over PNG: a full-page PNG runs to several megabytes where this
      // lands in the hundreds of kilobytes.
      filter: (node) =>
        !(node instanceof Element && node.hasAttribute('data-feedback-trigger')),
    })
    return blob.size > SCREENSHOT_MAX_BYTES ? null : blob
  } catch {
    return null
  }
}

// Dialogs can open the shell's existing widget without duplicating its form.
const OPEN_EVENT = 'mesterbrev:feedback-open'
export function FeedbackButton() {
  const t = useT()
  const { feedback } = usePage().props as { feedback?: FeedbackShare }
  if (!feedback?.enabled) return null
  return <Button icon="flag" data-feedback-trigger="" onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}>{t('feedback.trigger')}</Button>
}

export function FeedbackWidget() {
  const t = useT()
  const { feedback } = usePage().props as { feedback?: FeedbackShare }

  const [open, setOpen] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [screenshot, setScreenshot] = useState<Blob | null>(null)
  const [attachScreenshot, setAttachScreenshot] = useState(true)
  const [category, setCategory] = useState('')
  const [body, setBody] = useState('')
  const [bodyError, setBodyError] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)
  const [failed, setFailed] = useState(false)
  const [sent, setSent] = useState(false)

  const categories = feedback?.categories ?? []

  // Start the passive error/navigation buffers as soon as the shell mounts,
  // so a report filed later carries what happened before the click.
  const enabled = feedback?.enabled === true
  useEffect(() => {
    if (enabled) installFeedbackDiagnostics()
  }, [enabled])

  const reset = useCallback(() => {
    setScreenshot(null)
    setAttachScreenshot(true)
    setCategory('')
    setBody('')
    setBodyError(undefined)
    setFailed(false)
    setSent(false)
  }, [])

  const start = useCallback(async () => {
    setCapturing(true)
    reset()
    // Captured before the dialog mounts — see the flow note above.
    const shot = await capturePage()
    setScreenshot(shot)
    setAttachScreenshot(shot !== null)
    setCapturing(false)
    setOpen(true)
  }, [reset])

  useEffect(() => {
    if (!enabled) return
    const handleOpen = () => { if (!capturing && !open) void start() }
    window.addEventListener(OPEN_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_EVENT, handleOpen)
  }, [enabled, capturing, open, start])

  const close = useCallback(() => {
    setOpen(false)
    reset()
  }, [reset])

  const submit = useCallback(async () => {
    if (body.trim() === '') {
      setBodyError(t('feedback.body_required'))
      return
    }

    setSubmitting(true)
    setFailed(false)

    const form = new FormData()
    form.append('category', category === '' ? 'bug' : category)
    form.append('body', body)
    form.append('page_path', window.location.pathname + window.location.search)
    form.append('diagnostics', JSON.stringify(collectFeedbackDiagnostics()))
    if (attachScreenshot && screenshot !== null) {
      form.append('screenshot', screenshot, 'screenshot.webp')
    }

    // jsonFetch resolves for every outcome, network failures included — the
    // result flag is the only signal, so there is nothing to catch here.
    const result = await jsonFetch(ENDPOINT, { method: 'POST', body: form })
    if (result.ok) {
      setSent(true)
    } else {
      setFailed(true)
    }
    setSubmitting(false)
  }, [attachScreenshot, body, category, screenshot, t])

  if (feedback?.enabled !== true) return null

  return (
    <>
      <button
        type="button"
        className="feedback-fab"
        data-feedback-trigger=""
        onClick={() => { if (!capturing) void start() }}
        aria-disabled={capturing || undefined}
        aria-label={t('feedback.trigger')}
      >
        <Icon name="flag" />
        <span className="feedback-fab__label">{t('feedback.trigger')}</span>
      </button>

      <Modal
        open={open}
        onClose={close}
        title={sent ? t('feedback.success_title') : t('feedback.title')}
        footer={
          sent ? (
            <Button variant="primary" onClick={close}>
              {t('feedback.close')}
            </Button>
          ) : (
            <>
              <Button onClick={close}>{t('feedback.cancel')}</Button>
              <Button variant="primary" onClick={submit} disabled={submitting}>
                {submitting ? t('feedback.submitting') : t('feedback.submit')}
              </Button>
            </>
          )
        }
      >
        {sent ? (
          <p className="feedback-success__body">{t('feedback.success_body')}</p>
        ) : (
          <div className="feedback-form">
            <p className="feedback-form__intro">{t('feedback.intro')}</p>

            <SelectMenu
              label={t('feedback.category_label')}
              value={category}
              onChange={setCategory}
              options={categories}
              placeholder={categories[0]?.label}
            />

            <Textarea
              label={t('feedback.body_label')}
              value={body}
              onChange={(e) => {
                setBody(e.target.value)
                setBodyError(undefined)
              }}
              rows={5}
              hint={t('feedback.body_placeholder')}
              error={bodyError}
            />

            {screenshot !== null && (
              <Checkbox
                checked={attachScreenshot}
                onChange={setAttachScreenshot}
                label={`${t('feedback.screenshot_label')} — ${t('feedback.screenshot_hint')}`}
              />
            )}

            {failed && <p className="feedback-form__error">{t('feedback.error')}</p>}
          </div>
        )}
      </Modal>
    </>
  )
}
